import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube, faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import ReactGA from "react-ga4";
import logo from '../assets/images/logos/text_logo_white.png';
import { config } from '../utils/constants';

const REACT_APP_API_URL = config.url.REACT_APP_API_URL; // Ensure this is set in your .env file

const Footer = () => {
    const [serverMessage, setServerMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Formik setup
    const formik = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            email: "",
        },
        validationSchema: Yup.object({
            firstName: Yup.string().trim().required("First name is required"),
            lastName: Yup.string().trim().required("Last name is required"),
            email: Yup.string().email("Invalid email address").required("Email is required"),
        }),
        onSubmit: (values, { resetForm }) => {
            axios
                .post(`${REACT_APP_API_URL}/capture/`, {
                    first_name: values.firstName,
                    last_name: values.lastName,
                    email: values.email,
                    type: 'newsletter',
                }, { withCredentials: true })
                .then((res) => {
                    setServerMessage(res.data.message); // Show success message
                    setIsSubmitted(true); // Hide form
                    ReactGA.event({
                        category: 'engagement',
                        action: 'form submission',
                        label: 'Newsletter Signup',
                    });
                })
                .catch((err) => {
                    if (err.response && err.response.status === 409) {
                        setServerMessage("Thank you for subscribing to our newsletter!"); // Show thank you message if already subscribed
                        setIsSubmitted(true);
                    } else {
                        setServerMessage("An error occurred. Please try again.");
                    }
                });
        },
    });

    return (
        <footer className="bg3 p-t-30 p-b-30 footer2">
            <div className="container">
                <div className="row">
                    {/* Logo Section */}
                    <div className="col-sm-6 col-lg-2 p-b-30">
                        <div className="p-t-10">
                            <a className="logo" href="/">
                                <img src={logo} alt="Molu Logo" className="footer-logo" />
                            </a>
                        </div>
                    </div>

                    {/* Categories Section */}
                    <div className="col-sm-6 col-lg-3 p-b-30">
                        <h4 className="stext-301 cl0 p-b-10">Categories</h4>
                        <ul>
                            <li><a href="/">Home</a></li>
                            <li><a href="/shop">Shop</a></li>
                            <li><a href="/blog">Blog</a></li>
                            <li><a href="/about">About</a></li>
                            {/* <li><a href="/social-responsibility">Social Responsibility</a></li> */}
                            <li><a href="/glossary">Glossary</a></li>
                            <li><a href="/contact">Contact</a></li>
                        </ul>
                    </div>

                    {/* Contact Section */}
                    <div className="col-sm-6 col-lg-3 p-b-30">
                        <h4 className="stext-301 cl0 p-b-10">GET IN TOUCH</h4>
                        <p className="stext-107 cl7 size-201">
                            Any questions? Let us know at 5473 Blair Rd Ste 100 PMB 895415 US Dallas, Texas 75231-4227 US or call us at (+1) 972 283 8880.
                        </p>
                        <div className="p-t-10 footer-social">
                            <a href="https://www.facebook.com/people/Molu-Hair-Growth/61576808587286/" className="social-icon fs-18 cl7 hov-cl1 trans-04 m-r-16" target="_blank">
                                <FontAwesomeIcon icon={faFacebookF} />
                            </a>
                            <a href="https://www.instagram.com/moluhairgrowth" className="social-icon fs-18 cl7 hov-cl1 trans-04 m-r-16" target="_blank">
                                <FontAwesomeIcon icon={faInstagram} />
                            </a>
                            <a href="https://www.youtube.com/channel/UCUVzLhFqQzavjNhlsPOH4Bg" className="social-icon fs-18 cl7 hov-cl1 trans-04 m-r-16" target="_blank">
                                <FontAwesomeIcon icon={faYoutube} />
                            </a>
                        </div>
                    </div>

                    {/* Newsletter Section */}
                    <div className="col-sm-6 col-lg-3 p-b-30">
                        <h4 className="stext-301 cl0 p-b-10">Newsletter</h4>

                        {/* Show form only if not submitted */}
                        {!isSubmitted ? (
                            <form onSubmit={formik.handleSubmit}>
                                <div className="wrap-input1 w-full p-b-4">
                                    {/* First Name */}
                                    <input
                                        className="input1 bg-none plh1 stext-107 cl7"
                                        type="text"
                                        name="firstName"
                                        placeholder="First Name"
                                        value={formik.values.firstName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.firstName && formik.errors.firstName ? (
                                        <div className="text-danger mt-1 text-sm p-b-10">{formik.errors.firstName}</div>
                                    ) : null}

                                    {/* Last Name */}
                                    <input
                                        className="input1 bg-none plh1 stext-107 cl7"
                                        type="text"
                                        name="lastName"
                                        placeholder="Last Name"
                                        value={formik.values.lastName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.lastName && formik.errors.lastName ? (
                                        <div className="text-danger mt-1  text-sm p-b-10">{formik.errors.lastName}</div>
                                    ) : null}

                                    {/* Email */}
                                    <input
                                        className="input1 bg-none plh1 stext-107 cl7"
                                        type="email"
                                        name="email"
                                        placeholder="email@example.com"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.email && formik.errors.email ? (
                                        <div className="text-danger mt-1 text-sm p-b-10">{formik.errors.email}</div>
                                    ) : null}

                                    <div className="focus-input1 trans-04"></div>
                                </div>

                                {/* Submit Button */}
                                
                                <div className="p-t-18">
                                    <button className="flex-c-m stext-101 cl0 size-103 bg10 bor1 hov-btn2 p-lr-15 trans-04" type="submit">
                                        Subscribe
                                    </button>
                                </div>
                            </form>
                        ) : (
                            /* Show message only after successful submission */
                            <p className="cl0 text-lg text-center p-t-20">{serverMessage}</p>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
